import { Controller } from 'stimulus';
import flatpickr from 'flatpickr';

class StoreController extends Controller {
  static targets = ['inventory', 'subscribe'];

  /**
   * Find the closest element which contains the data inventory (publisher), denoted by:
   * `data-target="store.inventory"`. If an inventory is found, pass that element to, and invoke,
   * `_applyValues`.
   *
   * @param {Event} e click event
   * @returns {void}
   */
  open(e) {
    const inventory = e.target.closest('[data-target~="store.inventory"]');

    if (inventory) {
      this._applyValues(inventory);
    }
  }

  /**
   * Given an inventory (publisher) element, iterate over the `sections` data attribute
   * (`data-store-sections="SECTION NAMES HERE"`) and for each SECTION name grab the value of the
   * data attribute (`data-store-SECTION="VALUE"`), grab the element which is subscribing to this
   * SECTION data (`data-target="store.subscribe" data-store-subscribe="SECTION"`) and apply the value
   * to that element.
   *
   * @param {HTMLElement} inventory HTML Element containing values as data attributes
   * to be applied to our subscribers.
   * @returns {void}
   */
  _applyValues(inventory) {
    const storeSections = inventory.dataset.storeSections.split(' ');

    storeSections.forEach((section) => {
      const value = this._getData(inventory, section);
      const subscribers = document.querySelectorAll(
        `[data-target~="store.subscribe"][data-store-subscribe="${section}"]`
      );

      this._setTextOrValue(subscribers || [], value || '');
    });
  }

  /**
   * Given an element and a key, access the `data-store-${key}` value on the given element.
   *
   * @param {HTMLElement} element HTML Element containing a value as a data-attribute
   * @param {String} key Name of the data attribute
   * @returns {String} value of given data attribute from given element
   */
  _getData(element, key) {
    // titleize key for camelcase accessing
    const property = key[0].toUpperCase() + key.slice(1);
    const value = element.dataset[`store${property}`];

    return value;
  }

  /**
   * Given a value and an array of elements which are subscribing to a specific data set,
   * apply the value to each element based on the `tagName` of that element.
   * By default, set the `textContent` of the given element, if it is not a FORM or INPUT.
   *
   * @param {Array<HTMLElement>} targets HTML Element containing a value as a data-attribute
   * @param {String} value Value to be placed in the given elements
   * @returns {void}
   */
  _setTextOrValue(targets, value) {
    targets.forEach((target) => {
      switch (target.tagName) {
      case 'FORM':
        target.action = value;

        return;
      case 'INPUT':
        if (target.type === 'checkbox') {
          target.checked = value === 'true' ? 'true' : '';

          return;
        }
        if (target.type === 'file') {
          target.value = '';

          return;
        }
        if (
          target.dataset.controller &&
          target.dataset.controller.includes('flatpickr')
        ) {
          flatpickr(target, { dateFormat: 'M j, Y' }).setDate(
            value,
            false,
            'Y-m-d'
          );

          return;
        }

        target.value = value;

        return;
      case 'SELECT':
        target.selected = value;

        return;
      default:
        target.textContent = value;
      }
    });
  }
}

export default StoreController;
